import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CircularProgress, Switch, TextField } from "@mui/material";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import Drawer from "../../../components/ui/Drawer";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import {
  useAddGroupManagerMutation,
  useAddGroupMemberMutation,
  useBulkAssignGoalsMutation,
  useGetManagedGroupQuery,
  useLookupPersonMutation,
} from "../../../api/managementApi";
import { authApi } from "../../../api/authApi";
import { SoftButton, cls } from "../../hatim/soft/SoftKit";
import GoalListBuilder, { LineRow, sectionClass } from "../../groups/bulkGoals/GoalListBuilder";
import StepDots from "../../groups/bulkGoals/StepDots";
import { goalItemMeta } from "../../groups/bulkGoals/goalItems";
import { noServerResponse } from "../../groups/bulkGoals/bulkSteps";
import { reasonText } from "../../groups/bulkGoals/previewText";
import { codeDigits, formatCode } from "./memberCode";
import {
  actorPermissionsFrom,
  addPersonSteps,
  addResultLine,
  alreadyInGroup,
  canAdvance,
  capToActor,
  emptyAddPersonForm,
  goalsStepPrimaryLabel,
  grantedPermissionsFrom,
  personInitials,
  stepAfterAddError,
  withIdentityChange,
} from "./addPerson/addPersonSteps";

const PERMISSION_ROWS = [
  { key: "canAddMembers", label: "ap_perm_add" },
  { key: "canRemoveMembers", label: "ap_perm_remove" },
];

/** Adımın başlığı ve açıklaması (çeviri anahtarları). */
function stepText(step, purpose) {
  if (step === "identify") return { title: "ap_step_identify", description: "ap_step_identify_desc" };
  if (step === "person") return { title: "ap_step_person", description: "ap_step_person_desc" };
  if (step === "goals") return { title: "ap_step_goals", description: "ap_step_goals_desc" };
  if (step === "permissions") return { title: "ap_step_permissions", description: "ap_step_permissions_desc" };
  return {
    title: "ap_step_confirm",
    description: purpose === "manager" ? "ap_step_confirm_manager_desc" : "ap_step_confirm_member_desc",
  };
}

function Empty({ children }) {
  return <p className={`px-4 py-1.5 text-sm ${cls.muted}`}>{children}</p>;
}

/**
 * Kişi Ekle sihirbazı (mobil add-person): e-posta + kişisel kodla kişiyi
 * bul, önizle (zaten grupta mı), üyede isteğe bağlı hedef ver, yöneticide
 * yetkileri seç, onayla. Sağdan açılan çekmecede; üstte adım noktaları,
 * altta sabit Geri/Vazgeç ve İleri. mode: "member" | "manager".
 */
export default function AddPersonModal({ open, mode, groupId, onClose, onAdded }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const purpose = mode === "manager" ? "manager" : "member";

  const [lookupPerson] = useLookupPersonMutation();
  const [addMember] = useAddGroupMemberMutation();
  const [addManager] = useAddGroupManagerMutation();
  const [bulkAssign] = useBulkAssignGoalsMutation();
  // Ekleyenin kendi yetkileri: grup verisi zaten önbellekte.
  const { data: groupData } = useGetManagedGroupQuery(
    { groupId },
    { skip: !open || purpose !== "manager" || !groupId }
  );
  const actorPermissions = actorPermissionsFrom(groupData?.data?.group?.managers, user?._id);

  const [form, setForm] = useState(emptyAddPersonForm);
  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [identifyError, setIdentifyError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  // Ekleme ya da hedef ataması cevapsız kaldı: sunucuda sürüyor olabilir.
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false);

  // Her açılış baştan.
  useEffect(() => {
    if (!open) return;
    inFlight.current = false;
    setForm(emptyAddPersonForm());
    setStepIndex(0);
    setBusy(false);
    setIdentifyError(null);
    setSubmitError(null);
    setResult(null);
    setUncertain(false);
  }, [open]);

  const steps = addPersonSteps(purpose);
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const text = stepText(step, purpose);
  const ready = canAdvance(step, purpose, form) && !busy && !uncertain;
  const found = form.person;
  const fullName = found ? `${found.person.name || ""} ${found.person.surname || ""}`.trim() : "";
  const lacks = (key) => Boolean(actorPermissions) && !actorPermissions[key];
  const effectivePermissions = capToActor(form.permissions, actorPermissions);

  const primaryLabel =
    step === "identify"
      ? t(busy ? "ap_searching" : "gw_next")
      : step === "goals"
        ? t(goalsStepPrimaryLabel(form))
        : isLast
          ? t(purpose === "manager" ? "ap_make_manager" : "ap_add_to_group")
          : t("gw_next");

  const lookup = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setIdentifyError(null);
    try {
      const res = await lookupPerson({
        groupId,
        email: form.email.trim(),
        code: codeDigits(form.code),
        purpose,
      }).unwrap();
      setForm((f) => ({ ...f, person: res?.data || null }));
      setStepIndex(1);
    } catch (err) {
      setIdentifyError(err?.message || t("ap_lookup_error"));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  /** Hedef ataması: gruba eklendikten sonra toplu atama tek üyeyle. */
  const assignGoals = async (memberId) => {
    const goalCount = form.goals.length;
    try {
      const res = await bulkAssign({
        groupId,
        memberIds: [String(memberId)],
        goals: form.goals,
        templateId: form.templateId,
      }).unwrap();
      const failed = res?.data?.failed || [];
      return {
        line: addResultLine({ purpose, fullName, goalCount, goalsFailed: failed.length > 0 }, t),
        reasons: failed.map((f) => reasonText(f.reason, t)),
        permissions: null,
      };
    } catch (err) {
      if (noServerResponse(err)) return "uncertain";
      return {
        line: addResultLine({ purpose, fullName, goalCount, goalsFailed: true }, t),
        reasons: err?.message ? [err.message] : [],
        permissions: null,
      };
    }
  };

  const finishWith = (next) => {
    setResult(next);
    onAdded && onAdded(purpose);
    // Yönetici kendini üye yaptıysa kendi hedefleri de tazelensin.
    if (found?.isSelf) dispatch(authApi.util.invalidateTags(["Me"]));
  };

  const submit = async () => {
    if (!found || inFlight.current) return;
    inFlight.current = true;
    let keepLocked = false;
    setBusy(true);
    setSubmitError(null);
    const email = form.email.trim();
    const code = codeDigits(form.code);
    try {
      const plainLine = addResultLine({ purpose, fullName, goalCount: 0, goalsFailed: false }, t);
      if (purpose === "manager") {
        const res = await addManager({ groupId, email, code, permissions: effectivePermissions }).unwrap();
        finishWith({ line: plainLine, reasons: [], permissions: grantedPermissionsFrom(res) });
        return;
      }
      await addMember({ groupId, email, code }).unwrap();
      if (form.goals.length === 0) {
        finishWith({ line: plainLine, reasons: [], permissions: null });
        return;
      }
      const outcome = await assignGoals(found.person.id);
      if (outcome === "uncertain") {
        onAdded && onAdded(purpose);
        keepLocked = true;
        setUncertain(true);
        return;
      }
      finishWith(outcome);
    } catch (err) {
      if (noServerResponse(err)) {
        // Cevap gelmedi: kişi sunucuda eklenmiş olabilir, ikinci kez gönderilmez.
        keepLocked = true;
        setUncertain(true);
        return;
      }
      const message = err?.message || t("manage_add_person_error");
      const target = stepAfterAddError(err?.status, stepIndex);
      if (target !== stepIndex) {
        // Kod bu arada yenilenmiş ya da e-posta / kod tutmuyor: baştan.
        setForm((f) => ({ ...f, person: null }));
        setStepIndex(target);
        setIdentifyError(message);
      } else {
        setSubmitError(message);
      }
    } finally {
      if (!keepLocked) inFlight.current = false;
      setBusy(false);
    }
  };

  const next = () => {
    if (!ready) return;
    setSubmitError(null);
    if (step === "identify") lookup();
    else if (isLast) submit();
    else setStepIndex((i) => i + 1);
  };

  const back = () => {
    if (busy) return;
    setSubmitError(null);
    if (stepIndex === 0) onClose();
    else setStepIndex((i) => i - 1);
  };

  const renderIdentify = () => (
    <form
      className="px-4 flex flex-col gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        next();
      }}
    >
      <TextField
        autoFocus
        fullWidth
        type="email"
        size="small"
        margin="dense"
        label={t("ap_email_label")}
        placeholder="ornek@email.com"
        value={form.email}
        disabled={busy}
        onChange={(e) => {
          setIdentifyError(null);
          const email = e.target.value;
          setForm((f) => withIdentityChange(f, { email }));
        }}
        inputProps={{ autoComplete: "off" }}
      />
      {/* maxLength yok: yapıştırılan uzun metin kesilip rakam kaybolmasın; codeDigits 8'de keser. */}
      <TextField
        fullWidth
        size="small"
        margin="dense"
        label={t("manage_add_person_code_label")}
        helperText={t("manage_add_person_code_help")}
        placeholder="0000 · 0000"
        value={formatCode(form.code)}
        disabled={busy}
        onChange={(e) => {
          setIdentifyError(null);
          const code = codeDigits(e.target.value);
          setForm((f) => withIdentityChange(f, { code }));
        }}
        inputProps={{ inputMode: "numeric", autoComplete: "off", style: { letterSpacing: 2 } }}
      />
      <p data-member-code-note className={`text-xs leading-4 ${cls.muted}`}>
        {t("member_code_one_time")}
      </p>
      {identifyError ? (
        <p role="alert" className={`text-sm ${cls.danger}`}>
          {identifyError}
        </p>
      ) : null}
      {/* Enter ile gönderim için görünmez düğme. */}
      <button type="submit" hidden aria-hidden tabIndex={-1} />
    </form>
  );

  const renderPerson = () =>
    found ? (
      <div data-person-preview>
        <div className="flex items-center gap-3 px-4 py-2">
          <span className={`w-12 h-12 rounded-full flex items-center justify-center text-base shrink-0 ${cls.avatar}`}>
            {personInitials(fullName)}
          </span>
          <div className="min-w-0">
            <div className={`text-[17px] truncate ${cls.title}`}>{fullName}</div>
            <div className={`text-[13px] truncate ${cls.muted}`}>{found.person.email}</div>
          </div>
        </div>
        {alreadyInGroup(purpose, found) ? (
          <p role="status" className={`mx-4 flex items-center gap-2 py-2.5 text-[13px] ${cls.row} ${cls.accent}`}>
            <InfoOutlinedIcon fontSize="small" />
            {t(purpose === "manager" ? "ap_already_manager" : "ap_already_member")}
          </p>
        ) : null}

        <h4 className={sectionClass}>{t("ap_goals")}</h4>
        {(found.goals || []).length === 0 ? (
          <Empty>{t("ap_no_goals")}</Empty>
        ) : (
          <ul>
            {found.goals.map((goal) => (
              <LineRow
                key={`${goal.label}|${goal.activity || "okuma"}`}
                icon={<FlagOutlinedIcon fontSize="small" />}
                title={goal.label}
                meta={goalItemMeta(goal, t)}
              />
            ))}
          </ul>
        )}

        <h4 className={sectionClass}>{t("ap_groups")}</h4>
        {(found.groupNames || []).length === 0 ? (
          <Empty>{t("ap_no_groups")}</Empty>
        ) : (
          <ul>
            {found.groupNames.map((name, index) => (
              <LineRow key={`${name}|${index}`} icon={<GroupsOutlinedIcon fontSize="small" />} title={name} />
            ))}
          </ul>
        )}

        <h4 className={sectionClass}>{t("ap_stats")}</h4>
        <ul>
          <LineRow
            icon={<EmojiEventsOutlinedIcon fontSize="small" />}
            title={`${found.stats?.longestStreak ?? 0} ${t("gw_days")}`}
            meta={t("ap_longest_streak")}
          />
          <LineRow
            icon={<LocalFireDepartmentOutlinedIcon fontSize="small" />}
            title={`${found.stats?.currentStreak ?? 0} ${t("gw_days")}`}
            meta={t("ap_current_streak")}
          />
          <LineRow
            icon={<ShowChartIcon fontSize="small" />}
            title={`%${found.stats?.completionRate ?? 0}`}
            meta={t("ap_last_30")}
          />
        </ul>
      </div>
    ) : null;

  const renderPermissions = () => {
    const anyLacking = PERMISSION_ROWS.some(({ key }) => lacks(key));
    return (
      <div className="px-4">
        <ul>
          {PERMISSION_ROWS.map(({ key, label }) => (
            <li key={key} className={`flex items-center justify-between gap-3 py-2 ${cls.row} ${lacks(key) ? "opacity-50" : ""}`}>
              <span className={`text-[15px] ${cls.title}`} id={`perm-${key}`}>
                {t(label)}
              </span>
              <Switch
                checked={Boolean(form.permissions[key]) && !lacks(key)}
                disabled={busy || lacks(key)}
                inputProps={{ "aria-labelledby": `perm-${key}` }}
                onChange={(e) => {
                  const value = e.target.checked;
                  setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: value } }));
                }}
              />
            </li>
          ))}
        </ul>
        {anyLacking ? <p className={`pt-2 text-[13px] ${cls.muted}`}>{t("ap_perm_capped")}</p> : null}
      </div>
    );
  };

  const permissionList = (permissions) => (
    <ul>
      {PERMISSION_ROWS.map(({ key, label }) => (
        <LineRow
          key={key}
          icon={permissions[key] ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
          title={t(label)}
        />
      ))}
    </ul>
  );

  const renderConfirm = () =>
    found ? (
      <div>
        <ul>
          <LineRow
            icon={
              <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm ${cls.avatar}`}>
                {personInitials(fullName)}
              </span>
            }
            title={fullName}
            meta={found.person.email}
          />
        </ul>
        {purpose === "member" ? (
          <>
            <h4 className={sectionClass}>{t("ap_goals")}</h4>
            {form.goals.length === 0 ? (
              <Empty>{t("ap_no_goals_given")}</Empty>
            ) : (
              <ul>
                {form.goals.map((goal) => (
                  <LineRow
                    key={`${goal.label}|${goal.activity || "okuma"}`}
                    icon={<FlagOutlinedIcon fontSize="small" />}
                    title={goal.label}
                    meta={goalItemMeta(goal, t)}
                  />
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <h4 className={sectionClass}>{t("ap_step_permissions")}</h4>
            {permissionList(effectivePermissions)}
          </>
        )}
      </div>
    ) : null;

  const renderStep = () => {
    if (step === "identify") return renderIdentify();
    if (step === "person") return renderPerson();
    if (step === "goals") {
      return (
        <GoalListBuilder
          goals={form.goals}
          onChange={(goals) => setForm((f) => ({ ...f, goals }))}
          templateId={form.templateId}
          onTemplateChange={(templateId) => setForm((f) => ({ ...f, templateId }))}
        />
      );
    }
    if (step === "permissions") return renderPermissions();
    return renderConfirm();
  };

  const done = Boolean(result) || uncertain;
  const title = t(purpose === "manager" ? "manage_add_person_modal_title_manager" : "manage_add_person_modal_title_member");

  const footer = done ? (
    uncertain ? (
      <SoftButton tone="soft" fullWidth onClick={onClose}>
        {t("bulk_back_to_group")}
      </SoftButton>
    ) : (
      <SoftButton tone="success" fullWidth onClick={onClose}>
        {t("bulk_done")}
      </SoftButton>
    )
  ) : (
    <div className="flex flex-col gap-2">
      {submitError ? (
        <p role="alert" className={`text-sm ${cls.danger}`}>
          {submitError}
        </p>
      ) : null}
      <div className="flex gap-2">
        <SoftButton tone={stepIndex === 0 ? "cancel" : "soft"} fullWidth onClick={back} disabled={busy}>
          {stepIndex === 0 ? t("manage_add_person_cancel") : t("gw_back")}
        </SoftButton>
        <SoftButton tone={isLast ? "success" : "primary"} fullWidth onClick={next} disabled={!ready}>
          {busy && step !== "identify" ? <CircularProgress size={18} color="inherit" /> : primaryLabel}
        </SoftButton>
      </div>
    </div>
  );

  return (
    <Drawer
      open={Boolean(open)}
      onClose={busy ? undefined : onClose}
      width="sm"
      title={title}
      subtitle={found && !done ? fullName : undefined}
      titleIcon={purpose === "manager" ? <ShieldOutlinedIcon fontSize="small" /> : <PersonAddAltIcon fontSize="small" />}
      footer={footer}
    >
      {done ? (
        <div className="flex flex-col items-center gap-4 px-6 py-10 text-center" data-add-result>
          <span
            className={`w-14 h-14 rounded-full flex items-center justify-center text-white ${
              uncertain ? "bg-terakkiNeutral dark:bg-white/10" : "bg-[#2E9E6A] dark:bg-[#34B37A]"
            }`}
          >
            {uncertain ? <HourglassEmptyIcon /> : <CheckIcon />}
          </span>
          <p className={`text-base leading-6 ${cls.title}`}>{uncertain ? t("bulk_uncertain") : result.line}</p>
          {!uncertain
            ? result.reasons.map((reason, index) => (
                <p key={`${reason}|${index}`} className={`text-[13px] ${cls.muted}`}>
                  {reason}
                </p>
              ))
            : null}
          {!uncertain && result.permissions ? (
            <div className="self-stretch -mx-6 text-left">
              <h4 className={sectionClass}>{t("ap_step_permissions")}</h4>
              {permissionList(result.permissions)}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <StepDots count={steps.length} index={stepIndex} />
          <div className="px-4 pb-1">
            <h3 className={`text-lg font-medium ${cls.title}`}>{t(text.title)}</h3>
            <p className={`text-[13px] leading-5 ${cls.muted}`}>{t(text.description)}</p>
          </div>
          <div className="pb-6">{renderStep()}</div>
        </>
      )}
    </Drawer>
  );
}
